import { Artwork } from '#base/artwork.js';
import {
  Font,
  FontFamilyFonts,
  JerseyFont,
  MonogramFont,
  MonogramFontConfig,
  MonogramFontFamily,
} from '#base/fonts.js';

export type AssetLibrary = {
  font: Font[];
  monogram: MonogramFontFamily[];
  jersey: Font[];
  artwork: Artwork[];
};

const getFee = (library, asset) => {
  if (library?.fees && asset?.feeId) {
    return library.fees.find((f) => f.id === asset.feeId);
  } else {
    return null;
  }
};

export const withFee = (lib, a, o) => {
  const fee = getFee(lib, a);
  return fee ? Object.assign(o || {}, { fee }) : o;
};

const withCommon = (lib, a, o) => {
  const { restrictionType, attributes } = a;
  return Object.assign(o || {}, { restrictionType, attributes }, withFee(lib, a, o));
};

export const createAssetLibraryFromServerData = (library): [AssetLibrary, any] => {
  const artwork = library ? library.assets.filter((a) => a.assetType === 'artwork') : [];
  const monogram = library ? library.assets.filter((a) => a.assetType === 'monogram') : [];
  const font = library ? library.assets.filter((a) => a.assetType === 'font') : [];
  const jersey = library ? library.assets.filter((a) => a.assetType === 'jersey') : [];

  // TODO get fees for monogram families
  const monogramFamily = Object.values(
    monogram.reduce((a, m) => {
      const { family, role } = m.attributes || {};
      if (family) {
        a[family] = Object.assign({}, a[family], {
          family,
          [role]: new MonogramFont(m.id, { name: m.assetName, file: m.fileUrl }),
        });
      }
      return a;
    }, {})
  ).map(({ family, ...members }) => new MonogramFontFamily(family, members as FontFamilyFonts));

  const monogramRegular = monogram.filter((m) => !m?.attributes?.family);

  const assets = {
    font: font.map((f) => {
      const font = new Font(f.id, { name: f.assetName, file: f.fileUrl });
      //font.load();
      return withCommon(library, f, font);
    }),

    monogram: monogramRegular
      .map((m) => {
        const config = MonogramFontConfig.find((c) => c.name === m.assetName);
        if (!config) return null;
        const monogram = new MonogramFont(
          m.id,
          Object.assign({}, config, { name: m.assetName, file: m.fileUrl })
        );
        //monogram.load();
        return withCommon(library, m, monogram);
      })
      .filter((m) => !!m)
      .concat(monogramFamily),

    jersey: jersey.map((f) => {
      const font = new JerseyFont(f.id, { name: f.assetName, file: f.fileUrl });
      //font.load();
      return withCommon(library, f, font);
    }),

    artwork: artwork.map((a) => {
      const artwork = new Artwork(a.id, { name: a.assetName, file: a.fileUrl });
      return withCommon(library, a, artwork);
    }),
  };

  const groups = ['artwork', 'monogram', 'font', 'jersey'].reduce((a, assetType) => {
    const assets = library ? library.assets.filter((a) => a.assetType === assetType) : [];
    const groups = library ? library.groups.filter((a) => a.assetType === assetType) : [];
    const withChildren = groups.map((g) =>
      Object.assign({}, g, {
        assets: assets.filter((a) => a.groups.includes(g.id)).map((a) => a.id),
        groups: groups
          .filter((c) => c.parentId === g.id || (c.parents || []).includes(g.id))
          .map((a) => a.id),
      })
    );

    const withChildrenById = withChildren.reduce((a, g) => {
      a[g.id] = g;
      return a;
    }, {});
    const assetsById = assets.reduce((a, asset) => {
      a[asset.id] = asset;
      return a;
    }, {});

    const hasAssets = (g) =>
      (g.assets || []).length > 0 || (g.groups || []).some((id) => hasAssets(withChildrenById[id]));

    const graphicTypes = (g) => {
      if (assetType !== 'artwork') return null;
      const gc = g.assets.reduce(
        (a, id) => a.concat(assetsById[id]?.attributes?.graphicType || ['color', 'engraved']),
        []
      );
      return [
        ...new Set((g.groups || []).reduce((a, id) => a.concat(graphicTypes(withChildrenById[id])), gc)),
      ];
    };

    const minFee = (g) => {
      const assetsWithFees = (g.assets || [])
        .map((id) => assetsById[id])
        .map((a) => {
          return { ...a, fees: library?.fees?.find((f) => f.id === a.feeId) };
        });

      const minAssetFeeIdx = assetsWithFees.reduce((a, v, i) => {
        if (!assetsWithFees[a]?.fees?.rate) return a;

        if (v.fees?.rate < assetsWithFees[a].fees?.rate) {
          return i;
        } else {
          return a;
        }
      }, 0);

      const minAssetFee = {
        rate: assetsWithFees[minAssetFeeIdx]?.fees?.rate || null,
        usualRate: assetsWithFees[minAssetFeeIdx]?.fees?.usualRate || null,
      };

      const minGroupFee = [...(g.groups || []).map((id) => withChildrenById[id]).map(minFee)].reduce(
        (a, v, _i) => {
          if ((!a?.rate || isNaN(a?.rate)) && (!v?.rate || isNaN(v?.rate))) return { rate: 0, usualRate: 0 };
          else if (!a?.rate || isNaN(a?.rate)) return v;
          else if (!v?.rate || isNaN(v?.rate)) return a;
          else if (a?.rate > v?.rate) return v;
          else return a;
        },
        minAssetFee
      );

      if (
        (!minAssetFee?.rate || isNaN(minAssetFee?.rate)) &&
        (!minGroupFee?.rate || isNaN(minGroupFee?.rate))
      )
        return { rate: 0, usualRate: 0 };
      else if (!minAssetFee?.rate || isNaN(minAssetFee?.rate)) return minGroupFee;
      else if (!minGroupFee?.rate || isNaN(minGroupFee?.rate)) return minAssetFee;
      else if (minAssetFee?.rate < minGroupFee?.rate) return minAssetFee;
      else return minGroupFee;
    };

    const withAssets = withChildren.map((g) =>
      Object.assign(g, {
        hasAssets: hasAssets(g),
        graphicType: graphicTypes(g),
        fee: minFee(g).rate,
        usualRate: minFee(g).usualRate,
      })
    );

    a[assetType] = withAssets;
    return a;
  }, {});

  return [assets, groups];
};
